import * as React from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import styles from './BlockButton.module.css';

const expandBorderColor = (s: React.CSSProperties): React.CSSProperties => {
  if (s.borderColor == null) return s;
  const { borderColor, ...rest } = s;
  return {
    ...rest,
    borderTopColor: borderColor,
    borderRightColor: borderColor,
    borderBottomColor: borderColor,
    borderLeftColor: borderColor,
  };
};

export enum ButtonType {
  button = 'button',
  submit = 'submit',
}

export enum ButtonTargetType {
  self = '_self',
  blank = '_blank',
}

export interface ButtonProps {
  onClick?: React.MouseEventHandler<Element>;
  href?: string;
  target?: string;
  type?: ButtonType;
  big?: boolean;
  disabled?: boolean;
  outline?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.HTMLProps<HTMLButtonElement>['style'];
  onMouseEnter?: React.MouseEventHandler<Element>;
  onMouseLeave?: React.MouseEventHandler<Element>;
}

const Button: React.FunctionComponent<ButtonProps> = ({
  onClick,
  href,
  target,
  type = ButtonType.button,
  big,
  disabled,
  outline,
  children,
  className = '',
  style,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [isHover, setIsHover] = React.useState(false);

  const handleMouseEnter: React.MouseEventHandler<Element> = (e) => {
    setIsHover(true);
    onMouseEnter && onMouseEnter(e);
  };
  const handleMouseLeave: React.MouseEventHandler<Element> = (e) => {
    setIsHover(false);
    onMouseLeave && onMouseLeave(e);
  };

  const {
    buttonsPrimaryDefault: buttonStyleDefault,
    buttonsPrimaryDisabled: buttonStyleDisabled,
    buttonsPrimaryHover: buttonStyleHover,
    buttonsSecondaryDefault: outlineStyleDefault,
    buttonsSecondaryDisabled: outlineStyleDisabled,
    buttonsSecondaryHover: outlineStyleHover,
  } = useTheme();

  const sPrimary: React.CSSProperties = {
    ...buttonStyleDefault,
    ...(isHover
      ? (buttonStyleHover ?? {
          borderColor: '#286090',
          backgroundColor: '#286090',
          color: '#fff',
        })
      : {}),
    ...(disabled
      ? (buttonStyleDisabled ?? {
          borderColor: '#cccccc',
          backgroundColor: '#cccccc',
          cursor: 'not-allowed',
        })
      : {}),
  };

  const sOutline: React.CSSProperties = {
    ...outlineStyleDefault,
    ...(isHover
      ? (outlineStyleHover ?? {
          borderColor: '#286090',
          color: '#286090',
        })
      : {}),
    ...(disabled
      ? (outlineStyleDisabled ?? {
          borderColor: '#ccc',
          cursor: 'not-allowed',
          color: '#ccc',
        })
      : {}),
  };

  const classes = clsx({
    [className]: !!className,
    [styles.button]: true,
    [styles.buttonOutline]: outline,
  });

  const sButton = expandBorderColor({
    ...(outline ? sOutline : sPrimary),
    width: big ? '100%' : 'auto',
    ...style,
  });

  if (href) {
    return (
      <a
        href={href}
        target={target || ButtonTargetType.blank}
        rel="noreferrer noopener"
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={sButton}
        className={classes}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      style={sButton}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      type={type}
      className={classes}
    >
      {children}
    </button>
  );
};

export default Button;
